import { createFileRoute, redirect } from '@tanstack/react-router'
import { authReady, useAuthStore } from '@/stores/auth.store'
import { SignupPage } from '@/features/auth'
import {
  isStudioPath,
  sanitizeRedirectPath,
  validateProjectLoginTarget,
} from '@/features/auth/signup/project-login-target'

interface LoginSearch {
  inviteWorkspaceId?: string
  inviteProjectId?: string
  /**
   * Same-origin path to return the user to after a successful sign-in. Set by
   * route guards that intercept an authenticated request from an unauthenticated
   * session (e.g. `/workspaces/open?…`). Passed through `sanitizeRedirectPath`
   * before it is used, so an attacker-controlled value cannot open-redirect off
   * the origin.
   */
  redirect?: string
}

/**
 * Login route — renders the signup screen outside the Shell layout (no
 * three-panel chrome). One screen handles both new signups and returning users,
 * since the auth methods are identical.
 *
 * beforeLoad redirects already-authenticated users to the workspace home.
 */
export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>): LoginSearch => {
    const invite = validateProjectLoginTarget(search) ?? {}
    const redirectTo = sanitizeRedirectPath(search.redirect)
    return redirectTo ? { ...invite, redirect: redirectTo } : invite
  },
  beforeLoad: async ({ search }) => {
    await authReady
    if (useAuthStore.getState().user) {
      if (search.inviteWorkspaceId && search.inviteProjectId) {
        throw redirect({
          to: '/w/$workspaceId/projects/$projectId',
          params: { workspaceId: search.inviteWorkspaceId, projectId: search.inviteProjectId },
        })
      }
      if (search.redirect) {
        throw isStudioPath(search.redirect)
          ? redirect({ href: search.redirect, reloadDocument: true })
          : redirect({ to: search.redirect })
      }
      throw redirect({ to: '/' })
    }
  },
  component: SignupPage,
})
